import { SubscriptionIntegration } from "@redotech/redo-model/subscription-integrations/subscription-integration";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { SubscriptionFrequencyBadge } from "./subscription-frequency-badge";
import { useEditSubscription } from "./use-subscription-queries";

interface EditSubscriptionModalProps {
  subscription: SubscriptionIntegration;
  customerEmail: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

type IntervalUnit = "day" | "week" | "month";

const INTERVAL_UNIT_OPTIONS: RedoListItem<IntervalUnit>[] = [
  { id: "day", text: "Day(s)", type: "text", value: "day" },
  { id: "week", text: "Week(s)", type: "text", value: "week" },
  { id: "month", text: "Month(s)", type: "text", value: "month" },
];

export const EditSubscriptionModal = memo(function EditSubscriptionModal({
  subscription,
  customerEmail,
  isOpen,
  onClose,
}: EditSubscriptionModalProps) {
  const editSubscription = useEditSubscription(customerEmail);

  const [quantity, setQuantity] = useState(subscription.quantity.toString());
  const [orderIntervalFrequency, setOrderIntervalFrequency] = useState(
    (subscription.orderIntervalFrequency ?? 1).toString(),
  );
  const [orderIntervalUnit, setOrderIntervalUnit] = useState<IntervalUnit>(
    subscription.orderIntervalUnit ?? "month",
  );
  const [nextChargeDate, setNextChargeDate] = useState<
    Temporal.PlainDate | undefined
  >(
    subscription.nextChargeScheduledAt
      ? Temporal.PlainDate.from(subscription.nextChargeScheduledAt)
      : undefined,
  );
  const [price, setPrice] = useState(subscription.price || "0.00");

  const selectedIntervalUnit = useMemo(
    () =>
      INTERVAL_UNIT_OPTIONS.find(
        (option) => option.value === orderIntervalUnit,
      ),
    [orderIntervalUnit],
  );

  const handleSubmit = async () => {
    try {
      const updates: {
        quantity?: number;
        orderIntervalFrequency?: number;
        orderIntervalUnit?: "day" | "week" | "month";
        nextChargeScheduledAt?: string;
        price?: string;
      } = {};

      const newQuantity = parseInt(quantity, 10);
      if (newQuantity !== subscription.quantity) {
        updates.quantity = newQuantity;
      }

      const newOrderIntervalFrequency = parseInt(orderIntervalFrequency, 10);
      const intervalFrequencyChanged =
        newOrderIntervalFrequency !==
        (subscription.orderIntervalFrequency ?? 1);
      const intervalUnitChanged =
        orderIntervalUnit !== subscription.orderIntervalUnit;

      if (intervalFrequencyChanged || intervalUnitChanged) {
        updates.orderIntervalFrequency = newOrderIntervalFrequency;
        updates.orderIntervalUnit = orderIntervalUnit;
      }

      const originalDate = subscription.nextChargeScheduledAt
        ? Temporal.PlainDate.from(subscription.nextChargeScheduledAt)
        : undefined;
      const dateChanged = !!(
        (nextChargeDate && !originalDate) ||
        (!nextChargeDate && originalDate) ||
        (nextChargeDate && originalDate && !nextChargeDate.equals(originalDate))
      );
      if (dateChanged && nextChargeDate) {
        updates.nextChargeScheduledAt = nextChargeDate.toString();
      }

      if (price !== (subscription.price || "0.00")) {
        updates.price = price;
      }

      await editSubscription.mutateAsync({
        subscriptionId: subscription.id,
        updates,
      });

      toast("Subscription updated successfully", { variant: "success" });
      onClose();
    } catch (error: any) {
      toast(`Failed to update subscription: ${error.message || error}`, {
        variant: "error",
      });
    }
  };

  const isSubmitting = editSubscription.isPending;

  return (
    <RedoModal
      hideCloseButton={isSubmitting}
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      title="Edit Subscription"
    >
      <Flex dir="column" gap="lg" p="lg">
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            Quantity
          </Text>
          <RedoTextInput
            inputTypeHint="number"
            min={1}
            setValue={setQuantity}
            size="sm"
            value={quantity}
          />
        </Flex>

        <Flex dir="column" gap="sm">
          <Flex align="center" gap="sm">
            <Text fontSize="sm" fontWeight="medium">
              Frequency
            </Text>
            <SubscriptionFrequencyBadge
              frequency={parseInt(orderIntervalFrequency, 10)}
              intervalUnit={orderIntervalUnit}
            />
          </Flex>
          <Flex gap="sm">
            <RedoTextInput
              inputTypeHint="number"
              min={1}
              placeholder="Every"
              setValue={setOrderIntervalFrequency}
              size="sm"
              value={orderIntervalFrequency}
            />
            <RedoSingleSelectDropdownInput
              disabled={isSubmitting}
              flexGrow
              options={INTERVAL_UNIT_OPTIONS}
              optionSelected={({ item }) => setOrderIntervalUnit(item.value)}
              selectedItem={selectedIntervalUnit}
              size="xs"
            />
          </Flex>
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            Next Charge Date
          </Text>
          <RedoSingleDatePicker
            buttonSize="sm"
            date={nextChargeDate}
            disabled={isSubmitting}
            setDate={setNextChargeDate}
          />
        </Flex>

        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium">
            Price
          </Text>
          <RedoTextInput
            placeholder="0.00"
            setValue={setPrice}
            size="sm"
            value={price}
          />
        </Flex>

        <Flex gap="md" justify="flex-end">
          <RedoButton
            disabled={isSubmitting}
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
          />
          <RedoButton
            disabled={isSubmitting}
            hierarchy="primary"
            onClick={handleSubmit}
            size="md"
            text={isSubmitting ? "Saving..." : "Save Changes"}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
